/*!
VueLayers
Web map Vue components with the power of OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.10.25
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _defineProperty = _interopDefault(require('babel-runtime/helpers/defineProperty'));
var __ol__ = _interopDefault(require('openlayers'));
var consts = require('./consts');

var _transforms;

var olproj = __ol__.proj;
var Projection = __ol__.proj.Projection;
var transform = olproj.transform,
    transformExtent = olproj.transformExtent,
    getProj = olproj.get,
    addProj = olproj.addProjection;
function createProj(options) {
  return new Projection(options);
}

/**
 * @param {number[]} coordinate
 * @param {ol.ProjectionLike} [projection]
 * @return {number[]}
 */
function pointToLonLat(coordinate) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return olproj.toLonLat(coordinate, projection);
}
/**
 * @param {number[]} coordinate
 * @param {ol.ProjectionLike} [projection]
 * @return {number[]}
 */
function pointFromLonLat(coordinate) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return olproj.fromLonLat(coordinate, projection);
}

function transformPoint(coordinate, sourceProjection, destProjection) {
  return transform(coordinate, sourceProjection, destProjection);
}

/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function lineToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (point) {
    return pointToLonLat(point, projection);
  });
}
/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function lineFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (point) {
    return pointFromLonLat(point, projection);
  });
}

function transformLine(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (point) {
    return transform(point, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function polygonToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (line) {
    return lineToLonLat(line, projection);
  });
}
/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function polygonFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (line) {
    return lineFromLonLat(line, projection);
  });
}

function transformPolygon(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (line) {
    return transformLine(line, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function multiPointToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (point) {
    return pointToLonLat(point, projection);
  });
}
/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function multiPointFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (point) {
    return pointFromLonLat(point, projection);
  });
}

function transformMultiPoint(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (point) {
    return transformPoint(point, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function multiLineToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (line) {
    return lineToLonLat(line, projection);
  });
}
/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function multiLineFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (line) {
    return lineFromLonLat(line, projection);
  });
}

function transformMultiLine(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (line) {
    return transformLine(line, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<Array<number[]>>>} coordinates
 * @param {ol.ProjectionLike} projection
 * @return {Array<Array<Array<number[]>>>}
 */
function multiPolygonToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (polygon) {
    return polygonToLonLat(polygon, projection);
  });
}
/**
 * @param {Array<Array<Array<number[]>>>} coordinates
 * @param {ol.ProjectionLike} projection
 * @return {Array<Array<Array<number[]>>>}
 */
function multiPolygonFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return coordinates.map(function (polygon) {
    return polygonFromLonLat(polygon, projection);
  });
}

function transformMultiPolygon(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (polygon) {
    return transformPolygon(polygon, sourceProjection, destProjection);
  });
}

/**
 * Transforms by geom type
 * @type {Object<string, function>}
 */
var transforms = (_transforms = {}, _defineProperty(_transforms, consts.GEOMETRY_TYPE.POINT, {
  toLonLat: pointToLonLat,
  fromLonLat: pointFromLonLat,
  transform: transformPoint
}), _defineProperty(_transforms, consts.GEOMETRY_TYPE.LINE_STRING, {
  toLonLat: lineToLonLat,
  fromLonLat: lineFromLonLat,
  transform: transformLine
}), _defineProperty(_transforms, consts.GEOMETRY_TYPE.POLYGON, {
  toLonLat: polygonToLonLat,
  fromLonLat: polygonFromLonLat,
  transform: transformPolygon
}), _defineProperty(_transforms, consts.GEOMETRY_TYPE.MULTI_POINT, {
  toLonLat: multiPointToLonLat,
  fromLonLat: multiPointFromLonLat,
  transform: transformMultiPoint
}), _defineProperty(_transforms, consts.GEOMETRY_TYPE.MULTI_LINE_STRING, {
  toLonLat: multiLineToLonLat,
  fromLonLat: multiLineFromLonLat,
  transform: transformMultiLine
}), _defineProperty(_transforms, consts.GEOMETRY_TYPE.MULTI_POLYGON, {
  toLonLat: multiPolygonToLonLat,
  fromLonLat: multiPolygonFromLonLat,
  transform: transformMultiPolygon
}), _transforms);

/**
 * @param {ol.Extent} extent
 * @param {ol.ProjectionLike} [projection=EPSG:3857]
 * @return {ol.Extent}
 */
function extentFromLonLat(extent) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return transformExtent(extent, consts.EPSG_4326, projection);
}

/**
 * @param {ol.Extent} extent
 * @param {ol.ProjectionLike} [projection=EPSG:3857]
 * @return {ol.Extent}
 */
function extentToLonLat(extent) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : consts.EPSG_3857;

  return transformExtent(extent, projection, consts.EPSG_4326);
}

exports.transform = transform;
exports.transformExtent = transformExtent;
exports.getProj = getProj;
exports.addProj = addProj;
exports.createProj = createProj;
exports.pointToLonLat = pointToLonLat;
exports.pointFromLonLat = pointFromLonLat;
exports.transformPoint = transformPoint;
exports.lineToLonLat = lineToLonLat;
exports.lineFromLonLat = lineFromLonLat;
exports.transformLine = transformLine;
exports.polygonToLonLat = polygonToLonLat;
exports.polygonFromLonLat = polygonFromLonLat;
exports.transformPolygon = transformPolygon;
exports.multiPointToLonLat = multiPointToLonLat;
exports.multiPointFromLonLat = multiPointFromLonLat;
exports.transformMultiPoint = transformMultiPoint;
exports.multiLineToLonLat = multiLineToLonLat;
exports.multiLineFromLonLat = multiLineFromLonLat;
exports.transformMultiLine = transformMultiLine;
exports.multiPolygonToLonLat = multiPolygonToLonLat;
exports.multiPolygonFromLonLat = multiPolygonFromLonLat;
exports.transformMultiPolygon = transformMultiPolygon;
exports.transforms = transforms;
exports.extentFromLonLat = extentFromLonLat;
exports.extentToLonLat = extentToLonLat;



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/ol-ext/proj.js
// module id = 171
// module chunks = 1